import { BLUE_900, CORAL_600, GREEN_600, GRAY_1000, WHITE, YELLOW_500 } from 'constants/styling/theme'

/**
 * The Adjust editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT) edits a small JSON document — never pixels. Every
 * geometry value is in SOURCE pixels of the unrotated photo, so the same document renders the preview (scaled down)
 * and the saved file (full size), and an undo step is a few hundred bytes.
 */

/**
 * Tool ids are Pintura's feature names (plus `filter`), so `selectedPinturaFeature`, analytics `editionType`
 * ('adjust - crop') and `editionSettings.pinturaFeature` keep the values they had with Pintura.
 */
export type AdjustToolId = 'finetune' | 'filter' | 'crop' | 'resize' | 'redact' | 'annotate' | 'sticker'

export interface AdjustToolDescriptor {
  id: AdjustToolId
  /** `photo_studio_editing:manual_tools.*` — the labels the Pintura Adjust already uses */
  labelKey: string
  isNew?: boolean
}

export const ADJUST_TOOLS: AdjustToolDescriptor[] = [
  { id: 'finetune', labelKey: 'manual_tools.FINE_TUNE' },
  { id: 'filter', labelKey: 'manual_tools.FILTERS', isNew: true },
  { id: 'crop', labelKey: 'manual_tools.CROP' },
  { id: 'resize', labelKey: 'manual_tools.RESIZE' },
  { id: 'redact', labelKey: 'manual_tools.BLUR' },
  { id: 'annotate', labelKey: 'manual_tools.ANNOTATE' },
  { id: 'sticker', labelKey: 'manual_tools.STICKER' },
]

export const isAdjustToolId = (value: unknown): value is AdjustToolId => ADJUST_TOOLS.some(tool => tool.id === value)

/**
 * Blur is a tool of its own in the Photo Studio (rail tile, Combine tools section) with the in-house editor: the same
 * editor and document, on this tool. The Adjust list leaves it out.
 */
export const ADJUST_BLUR_TOOL: AdjustToolId = 'redact'
export const ADJUST_PANEL_TOOLS = ADJUST_TOOLS.filter((tool) => tool.id !== ADJUST_BLUR_TOOL)

/** Same ranges as the in-house editor: −100…100 (clarity 0…100), gamma as a multiplier 0.1…3 */
export interface AdjustAdjustments {
  exposure: number
  brightness: number
  contrast: number
  highlights: number
  shadows: number
  gamma: number
  temperature: number
  tint: number
  saturation: number
  clarity: number
  vignette: number
}

export type AdjustmentKey = keyof AdjustAdjustments

export const DEFAULT_ADJUSTMENTS: AdjustAdjustments = {
  exposure: 0,
  brightness: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  gamma: 1,
  temperature: 0,
  tint: 0,
  saturation: 0,
  clarity: 0,
  vignette: 0,
}

export interface AdjustmentControl {
  key: AdjustmentKey
  min: number
  max: number
  /** Not in Pintura's Fine tune */
  isNew?: boolean
}

/** Gamma is edited as 10…300 and stored as 0.1…3 */
export const GAMMA_SCALE = 100

export const ADJUSTMENT_GROUPS: Array<{ id: 'light' | 'color' | 'detail', controls: AdjustmentControl[] }> = [
  {
    id: 'light',
    controls: [
      { key: 'exposure', min: -100, max: 100 },
      { key: 'brightness', min: -100, max: 100 },
      { key: 'contrast', min: -100, max: 100 },
      { key: 'highlights', min: -100, max: 100, isNew: true },
      { key: 'shadows', min: -100, max: 100, isNew: true },
      { key: 'gamma', min: 10, max: 300 },
    ],
  },
  {
    id: 'color',
    controls: [
      { key: 'temperature', min: -100, max: 100 },
      { key: 'tint', min: -100, max: 100, isNew: true },
      { key: 'saturation', min: -100, max: 100 },
    ],
  },
  {
    id: 'detail',
    controls: [
      { key: 'clarity', min: 0, max: 100 },
      { key: 'vignette', min: -100, max: 100 },
    ],
  },
]

/** The 7 looks curated for listings (product decision 2026-10-08), deltas from the in-house editor's presets */
export type AdjustLookId = 'original' | 'warm' | 'cool' | 'vivid' | 'dramatic' | 'matte' | 'bw'

export const ADJUST_LOOKS: Array<{ id: AdjustLookId, adjustments: Partial<AdjustAdjustments> }> = [
  { id: 'original', adjustments: {} },
  { id: 'warm', adjustments: { temperature: 30, saturation: 10, contrast: 5 } },
  { id: 'cool', adjustments: { temperature: -30, saturation: 5, contrast: 5 } },
  { id: 'vivid', adjustments: { saturation: 40, contrast: 20, clarity: 20 } },
  { id: 'dramatic', adjustments: { contrast: 30, clarity: 40, shadows: -20, highlights: 20 } },
  { id: 'matte', adjustments: { contrast: -10, shadows: 20, saturation: -10 } },
  { id: 'bw', adjustments: { saturation: -100, contrast: 20 } },
]

export interface AdjustLook {
  id: AdjustLookId
  /** 0…100 */
  intensity: number
}

/** Today's Pintura crop ratios (Custom, Square, landscape + portrait versions), plus the photo's own ratio */
export type CropAspectId = 'custom' | 'original' | 'square' | '2:1' | '3:2' | '4:3' | '5:4' | '16:10' | '16:9'

export const CROP_ASPECTS: Array<{ id: CropAspectId, ratio: number | null }> = [
  { id: 'custom', ratio: null },
  { id: 'original', ratio: null },
  { id: 'square', ratio: 1 },
  { id: '2:1', ratio: 2 },
  { id: '3:2', ratio: 3 / 2 },
  { id: '4:3', ratio: 4 / 3 },
  { id: '5:4', ratio: 5 / 4 },
  { id: '16:10', ratio: 16 / 10 },
  { id: '16:9', ratio: 16 / 9 },
]

/** Crop frame in FRAME space: the photo flipped, then rotated about its centre; origin = photo centre, source px */
export interface AdjustCropRect {
  cx: number
  cy: number
  width: number
  height: number
}

export interface AdjustCrop {
  aspect: CropAspectId
  /** Swaps a landscape ratio to its portrait version */
  portrait: boolean
  /** Fine rotation in degrees, −45…45 */
  straighten: number
  /** Quarter turns clockwise (any integer; normalised when used) */
  quarterTurns: number
  flipX: boolean
  flipY: boolean
  /** null = the largest frame of the ratio, centred */
  rect: AdjustCropRect | null
}

export const STRAIGHTEN_LIMIT = 45

/** Output size, applied after the crop. Never larger than the crop. */
export interface AdjustResize {
  /** Width = round(crop width × scale) */
  scale: number
  /** Height follows the crop's ratio */
  lock: boolean
  /** Explicit height while unlocked */
  height: number | null
}

export type BlurMode = 'area' | 'brush'

export interface AdjustBlur {
  id: string
  mode: BlurMode
  /** Blur radius, source px */
  radius: number
  /** Brush diameter, source px (brush only) */
  size: number
  /** area: [x0, y0, x1, y1]; brush: flat polyline [x, y, x, y, …] */
  points: number[]
}

export type DrawTool = 'pen' | 'line' | 'arrow' | 'rectangle' | 'ellipse' | 'eraser'

export interface AdjustShape {
  id: string
  tool: DrawTool
  color: string
  /** Stroke width, source px */
  size: number
  /** 0…1 */
  opacity: number
  /** pen / eraser: flat polyline; others: [x0, y0, x1, y1] */
  points: number[]
}

export type StickerKind = 'icon' | 'text' | 'logo'

/** Real-estate icons (product decision 2026-10-08: no emoji — the brand guide rules them out) */
export type StickerIconId =
  | 'home' | 'bed' | 'bathtub' | 'square_foot' | 'key' | 'sell' | 'location' | 'parking'
  | 'pool' | 'sun' | 'park' | 'kitchen' | 'balcony' | 'fireplace' | 'stairs' | 'star'

export const STICKER_ICONS: StickerIconId[] = [
  'home', 'bed', 'bathtub', 'square_foot', 'key', 'sell', 'location', 'parking',
  'pool', 'sun', 'park', 'kitchen', 'balcony', 'fireplace', 'stairs', 'star',
]

export type StickerFont = 'sans' | 'brand'

export interface AdjustSticker {
  id: string
  kind: StickerKind
  icon?: StickerIconId
  text?: string
  font?: StickerFont
  /** Key into the editor's logo registry (uploaded images are kept out of the document) */
  logoId?: string
  color: string
  /** Centre, source px */
  x: number
  y: number
  width: number
  height: number
  /** Degrees, relative to the unrotated photo */
  rotation: number
  /** 0…1 */
  opacity: number
  flipX: boolean
}

export interface AdjustDocument {
  adjustments: AdjustAdjustments
  look: AdjustLook
  crop: AdjustCrop
  resize: AdjustResize
  blurs: AdjustBlur[]
  shapes: AdjustShape[]
  stickers: AdjustSticker[]
}

export const DEFAULT_CROP: AdjustCrop = {
  aspect: 'custom',
  portrait: false,
  straighten: 0,
  quarterTurns: 0,
  flipX: false,
  flipY: false,
  rect: null,
}

export const DEFAULT_RESIZE: AdjustResize = { scale: 1, lock: true, height: null }

export const createAdjustDocument = (): AdjustDocument => ({
  adjustments: { ...DEFAULT_ADJUSTMENTS },
  look: { id: 'original', intensity: 100 },
  crop: { ...DEFAULT_CROP },
  resize: { ...DEFAULT_RESIZE },
  blurs: [],
  shapes: [],
  stickers: [],
})

/**
 * Sizes on the panel's sliders are in UNITS of 1/1000 of the photo's long edge, so a brush or a blur looks the same
 * on a 1,000 px and a 6,000 px photo. The document stores source px.
 */
export const unitOf = (width: number, height: number) => Math.max(width, height) / 1000

/** Blur strength 1…10 → radius in units (BLUR_UNITS_PER_STRENGTH each): 1 only softens, Area's default hides plates and faces */
export const BLUR_STRENGTH_MIN = 1
export const BLUR_STRENGTH_MAX = 10
/** Default strength per mode: Area (privacy rectangles) blurs hard, Brush starts lighter */
export const BLUR_STRENGTH_DEFAULT: Record<BlurMode, number> = { area: 6, brush: 3 }
export const BLUR_UNITS_PER_STRENGTH = 4
export const BLUR_BRUSH_SIZE_RANGE = { min: 10, max: 150, default: 40 }

export const DRAW_SIZE_RANGE = { min: 2, max: 40, default: 8 }

/** Brand-safe colours for drawing and stickers */
export const ADJUST_SWATCHES: string[] = [WHITE, GRAY_1000, CORAL_600, BLUE_900, GREEN_600, YELLOW_500, '#E85E5E', '#EAE4D7']

export const DEFAULT_DRAW_COLOR = CORAL_600
export const DEFAULT_STICKER_COLOR = WHITE

/** Longest edge of the preview the canvas renders (the save always renders the full photo) */
export const PREVIEW_MAX_EDGE = 2560

export const ADJUST_EXPORT_JPEG_QUALITY = 0.92
